// Angular
import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations';
import { NavigationEnd, Router } from '@angular/router';
// RxJS
import { Subscription } from 'rxjs';

/**
 * Page load animation
 *
 */
@Directive({
	selector: '[ktContentAnimate]'
})
export class ContentAnimateDirective implements OnInit, OnDestroy {
	// Public properties
	player: AnimationPlayer;
	// Private properties
	private events: Subscription;

	/**
	 * Directive Consturctor
	 * @param el: ElementRef
	 * @param router: Router
	 * @param animationBuilder: AnimationBuilder
	 */
	constructor(
		private el: ElementRef,
		private router: Router,
		private animationBuilder: AnimationBuilder) {
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		// animate the content
		this.initAnimate();
		// animate page load
		this.events = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				// Rebuild the player (instead of replaying the same
				// instance) so its onDone callback - which clears the
				// residual transform below - fires on every navigation,
				// not just the first. AnimationPlayer.onDone only
				// reliably fires once per player instance; reusing the
				// same player across route changes left the transform
				// uncleared on the second+ navigation, which made
				// fixed-position slide panels (cr5010-new, ba1300-new,
				// da1300-new) render underneath the top header bar
				// intermittently depending on navigation order.
				this.player.destroy();
				this.initAnimate();
				this.player.play();
			}
		});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.events.unsubscribe();
		this.player.destroy();
	}

	/**
	 * Animate page load
	 */
	initAnimate() {
		// Opacity-only fade - deliberately no `transform` step. Per the
		// CSS spec, ANY non-'none' transform on an ancestor (even a
		// transient translateY(0%) left behind after the animation
		// "finishes") establishes a new containing block for
		// position:fixed descendants. That was pulling fixed-position
		// slide panels (cr5010-new, ba1300-new, da1300-new) out of true
		// viewport coordinates, rendering them underneath the top
		// header bar. Clearing the inline style after the fact was
		// timing-sensitive and unreliable (AnimationPlayer.onDone does
		// not fire the same way on every replay) - removing transform
		// from the animation entirely avoids the problem at its source.
		this.player = this.animationBuilder
			.build([
				style({opacity: 0}),
				animate('0.5s ease-in-out', style({opacity: 1}))
			])
			.create(this.el.nativeElement);
	}
}
